import { ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { routes } from "../routes";

import dayjs from "dayjs";
import { useQuery } from "react-query";
import { User } from "../../api/user";
import { WithLoginToken } from "../../hoc/WithLoginToken";
import { useAuth } from "../../hooks/useAuth";
import HubspotTracker from "../../hooks/useHubspotTracking";
import PostHogTracker from "../../hooks/usePostHogTracking";
import {
  setInitialSpeedUnit,
  setInitialUnitSystem,
  setIsUserLoading,
  setUser,
} from "../../store/reducers/user.reducer";

import { UserActivityLogProvider } from "../../hooks/useUserActivityLog";
import posthog from "../../posthog";

function GuardRoute({ children }: { children: ReactElement }) {
  const location = useLocation();
  const navigate = useNavigate();
  //get Auth user
  const isAuth = useSelector<RootState>(
    (state) => state.user.isAuthenticated,
  ) as boolean;
  const maintenanceMode = useSelector<RootState, boolean>(
    (state) => state.user.maintenance,
  );
  const restricted = useSelector<RootState>(
    (state) => state.user.restrictedMode,
  ) as boolean;
  const exam = useSelector<RootState>((state) => state.user.restrictedExam);
  const token = localStorage.getItem("share_token");
  const { getPlatformConfig, disconnect } = useAuth();

  const queryParams = restricted
    ? `?training=${exam}&token=${token}`
    : `?${encodeURIComponent("additional-groups[]")}=user:analyticsViews&${encodeURIComponent("additional-groups[]")}=analyticsView:list`;
  const dispatch = useDispatch();

  const authQuery = useQuery(
    ["auth", JSON.stringify(queryParams)],
    () => User.getUser(queryParams),
    {
      enabled: isAuth === true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      retry: 3,
      onSuccess: (response) => {
        //Set user data to local redux
        dispatch(setUser(response.data));
        //Get the platform config
        getPlatformConfig(response.data["@id"]);
        //Set user lang and default units
        dayjs.locale(response.data.lang);
        dispatch(setInitialSpeedUnit(response.data.default_speed_unit));
        dispatch(setInitialUnitSystem(response.data.unit_system));
        dispatch(setIsUserLoading(false));

        //Identify user for posthog
        posthog.identify(response.data["@id"], {
          name: response.data.full_name,
          email: response.data.email,
        });
      },
      onError: () => {
        dispatch(setIsUserLoading(false));
      },
    },
  );

  useEffect(() => {
    dispatch(setIsUserLoading(authQuery.isLoading && !maintenanceMode));
  }, [authQuery.isLoading, maintenanceMode]);



  useEffect(() => {
    const isNotTraining = !location.pathname.startsWith("/training");

    if (isAuth && restricted && isNotTraining) {
      disconnect();
    }
  }, [restricted, isAuth]);

  if (!isAuth) {
    let nextLocation: string | undefined = location.pathname;

    let guestRoutes = Object.values(routes.AUTH_ROUTES) as string[];

    //If next location is /logout or some guest routes dont save it
    if (guestRoutes.includes(nextLocation)) {
      nextLocation = undefined;
    }

    return (
      <Navigate
        to={routes.AUTH_ROUTES.LOGIN}
        state={{ nextLocation }}
      ></Navigate>
    );
  }

  return (
    <>
      <HubspotTracker />
      <PostHogTracker />
      <UserActivityLogProvider>{children}</UserActivityLogProvider>
    </>
  );
}

export default WithLoginToken(GuardRoute);
